import { useEffect, useState, type FC } from 'react';

import {
  Button,
  Typography,
  Icon,
  Toastify,
  toast,
  checkEmpty,
} from 'pixel-react';
import { useNavigate } from 'react-router-dom';

import { useReSendActivationMutation } from '@/store/resetActivationLink/resetActivationLinkSlice';
import type { SuccessState } from '@/types/expiredActivation';
import type { ResendActivationLinkResponse } from '@/types/resendActivation';

import styles from './ExpiredActivationLink.module.scss';
import { SignUpWrapper } from '../SignUpWrapper';

export const ExpiredActivationLink: FC = () => {
  const clientName = import.meta.env.VITE_CLIENT_DETAIL?.name;
  // function isEmptyValue(val: string | null): boolean {
  //   return val === null || val.length <= 0;
  // }
  const [success, setSuccess] = useState<SuccessState>({
    open: false,
    message: '',
  });

  const [email, setEmail] = useState<string | null>('');
  const [showActiveUserText, setActiveUserText] = useState<SuccessState>({
    open: false,
    message: '',
  });
  useEffect(() => {
    let search = window.location.search;
    // Fix the URL if there are multiple '?'
    if (search.indexOf('?') !== search.lastIndexOf('?')) {
      const parts = search.split('?');
      search = '?' + parts.slice(1).join('&');
    }
    const emailId = new URLSearchParams(search).get('emailId');
    const page = new URLSearchParams(search).get('page');

    setEmail(emailId);
    const activationStatus = new URLSearchParams(search).get(
      'activationStatus',
    );

    if (activationStatus === 'ACTIVE' && checkEmpty(page)) {
      setActiveUserText({ open: true, message: 'User is already activated' });
    }
  }, []);

  const navigate = useNavigate();
  const handleSignIn = async () => {
    navigate('/signin');
  };
  const [resendActivationLink] = useReSendActivationMutation();
  function verifyIsUserActive(response: ResendActivationLinkResponse) {
    if (
      response.responseCode === 400 &&
      response.message === 'User is already activated'
    ) {
      setActiveUserText({ open: true, message: response.message });
    } else if (response.responseObject) {
      setSuccess({ open: true, message: '' });
    }
  }
  const handleResendLink = async () => {
    try {
      const payLoad = { emailId: email };
      const response = (await resendActivationLink(
        payLoad,
      ).unwrap()) as ResendActivationLinkResponse;
      if (response.responseCode === 200) {
        toast.success('Activation link sent successfully');
      } else {
        toast.info(response.message ?? '');
      }

      if (response.message === 'User is already activated') {
        setActiveUserText({ open: true, message: response.message });
      }
      if (response.responseObject) {
        verifyIsUserActive(response?.responseObject);
      }
      setTimeout(() => {
        setSuccess({ open: false, message: '' });
      }, 5000);
    } catch (error) {
      console.error('Login failed. Please check your credentials.', error);
    }
  };
  return (
    <SignUpWrapper
      content_header={
        <>
          {' '}
          <Typography fontSize="24px" fontWeight="bold">
            Enter the world of high speed, scriptless test automation with{' '}
            <br />
            {clientName} AI
          </Typography>{' '}
        </>
      }
      sub_content={`You are one step away from enriching your testing process with one of the world’s best test automation platforms - ${clientName} AI `}
      footer_content={`${clientName}: High Speed Testing, Simplified!`}
      copyright_content={`Copyright © ${clientName} Pvt Ltd. All Rights Reserved`}
      className={styles.activationlink_exp_container}
    >
      <section className={styles.form_container}>
        <aside className={styles.exp_activationlink_form}>
          <section className={styles['icon_container']}>
            <Icon color="" height={240} width={240} name="link_expired" />
          </section>
          <section className={styles.exp_activationlink_header}>
            <Typography
              fontSize="32px"
              textAlign="center"
              className={styles['header_text']}
            >
              Your Activation link is expired
            </Typography>
            {showActiveUserText.open ? (
              <Typography
                fontSize="16px"
                textAlign="center"
                lineHeight="22px"
                className={styles['sub_header_text']}
              >
                Your {clientName} account is active. Please visit login page to
                get started with {clientName}
              </Typography>
            ) : success.open ? (
              <Typography
                fontSize="16px"
                textAlign="center"
                lineHeight="22px"
                className={styles['sub_header_text']}
              >
                Please check{' '}
                <Typography
                  fontSize="16px"
                  textAlign="center"
                  lineHeight="22px"
                  className={styles['highlighted_text']}
                >
                  {email}
                </Typography>{' '}
                for a latest link or click on
              </Typography>
            ) : (
              <Typography
                fontSize="16px"
                textAlign="center"
                lineHeight="22px"
                className={styles['sub_header_text']}
              >
                To proceed, You may need to request a new activation link by
                clicking on "Resend Activation Link" button.
              </Typography>
            )}
          </section>

          <div className={styles.exp_activationlink_footerBtn}>
            {showActiveUserText.open ? (
              <Button
                buttonWidth="216px"
                type="submit"
                variant="primary"
                onClick={handleSignIn}
              >
                <Typography fontSize="16px" className={styles['register_btn']}>
                  Proceed to Sign In
                </Typography>
              </Button>
            ) : (
              <Button
                type="submit"
                variant="primary"
                onClick={handleResendLink}
                buttonWidth="216px"
              >
                <Typography fontSize="16px" className={styles['register_btn']}>
                  Resend Activation Link
                </Typography>
              </Button>
            )}
          </div>
        </aside>
      </section>
      <Toastify />
    </SignUpWrapper>
  );
};
